import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import CheckmarkCircleIcon from '../../../../assets/icons/CheckmarkCircleIcon';
import WarningTriangleIcon from '../../../../assets/icons/WarningTriangleIcon';
import Button from '../../../buttons/Button';

import { PickTour, PaginationData } from '../../../../types';
import { SkipItemsAction } from '../../../../redux/sagas/types';
import { mergeClassNames, calculateSkippedQuantity, groupPicksByOrderStatus } from '../../../../utils';
import { LANGUAGE_SPANISH, SKIP_ITEMS_ACTION_EDIT_ORDER, SKIP_ITEMS_ACTION_FINALIZE } from '../../../../constants';

import styles from './PickTourComplete.css';

type Props = {
  activePickTour: PickTour;
  paginationData: PaginationData;
  returnToOrderList: () => void;
  currentLanguage: string;
  commercialSkipItemsAction: SkipItemsAction;
  editingOrders: Array<{ orderId: string; shopName: string }>;
};

const CommercialPickTourComplete = ({
  activePickTour,
  paginationData,
  returnToOrderList,
  currentLanguage,
  commercialSkipItemsAction,
  editingOrders,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const translationRoot = 'PICK_TOUR_COMPLETE_OR_EXIT';

  const completedQuantity =
    activePickTour.picks
      .filter(pick => pick.isEligibleForPicking)
      .reduce((quantity, pick) => quantity + pick.pickedQuantity, 0) +
    (paginationData.isPaginated ? paginationData.totalItemsPicked - paginationData.initialPageItemsPicked : 0);

  const skippedQuantity = paginationData.isPaginated
    ? paginationData.totalItemsSkipped
    : calculateSkippedQuantity(activePickTour);

  // Calculate picked quantity only from orders that were finalized (not in editingOrders)
  const completedQuantityFromFinalizedOrders =
    activePickTour.picks
      .filter(pick => pick.isEligibleForPicking)
      .filter(pick => !editingOrders.some(editedOrder => editedOrder.orderId === pick.orderID))
      .reduce((quantity, pick) => quantity + pick.pickedQuantity, 0) +
    (paginationData.isPaginated ? paginationData.totalItemsPicked - paginationData.initialPageItemsPicked : 0);

  // Calculate order summaries and filter out orders in editingOrders
  const orderSummaries = groupPicksByOrderStatus(activePickTour.picks);
  const successfullyPickedOrders = orderSummaries.filter(
    order => order.isFullyPicked && !editingOrders.some(editedOrder => editedOrder.orderId === order.orderID)
  );
  const unsuccessfullyPickedOrders = orderSummaries.filter(
    order => !order.isFullyPicked && !editingOrders.some(editedOrder => editedOrder.orderId === order.orderID)
  );

  const nonPickablePicks = activePickTour.picks.filter(pick => !pick.isEligibleForPicking);
  const nonPickableQuantity = nonPickablePicks.reduce((total, pick) => total + pick.totalQuantity, 0);

  const shouldShowNonPickableMessage = (() => {
    if (!nonPickableQuantity) return false;

    if (commercialSkipItemsAction === SKIP_ITEMS_ACTION_EDIT_ORDER) {
      // Edit Order: show if successfully picked orders contain non-pickable items
      return nonPickablePicks.some(pick => successfullyPickedOrders.some(order => order.orderID === pick.orderID));
    }
    if (commercialSkipItemsAction === SKIP_ITEMS_ACTION_FINALIZE) {
      // Finalize: show if orders NOT in editingOrders contain non-pickable items
      return nonPickablePicks.some(pick => !editingOrders.some(editedOrder => editedOrder.orderId === pick.orderID));
    }

    return false;
  })();

  // Reusable function to render order status sections
  const renderOrderSection = ({
    orders,
    titleKey,
    messageKey,
    isSuccess,
    keyField = 'orderID',
    destinationField = 'destination',
  }: {
    orders: any[];
    titleKey: string;
    messageKey: string;
    isSuccess: boolean;
    keyField?: string;
    destinationField?: string;
  }) => {
    if (orders.length === 0) return null;

    return (
      <div className={styles.lineItem}>
        <div className={styles.lineItemContainer}>
          <div className={styles.lineItemTitleContainer}>
            <div className={isSuccess ? styles.lineItemIconContainerSuccess : styles.lineItemIconContainerUnsuccess}>
              {isSuccess ? <CheckmarkCircleIcon /> : <WarningTriangleIcon />}
            </div>
            <span>{t(`${translationRoot}.${titleKey}`)}</span>
          </div>
          <h6 className={styles.lineItemCount}>{orders.length}</h6>
        </div>
        {orders.map(order => (
          <div key={order[keyField]} className={styles.orderDestination}>
            {order[destinationField]}
          </div>
        ))}
        <p className={isSuccess ? styles.lineItemMessageSuccess : styles.lineItemMessageUnsuccess}>
          {t(`${translationRoot}.${messageKey}`)}
        </p>
      </div>
    );
  };

  const lineItems = [
    ...(completedQuantity > 0
      ? [
          {
            label: 'ITEMS_PICKED',
            subLabel: '',
            quantity: completedQuantity,
          },
        ]
      : []),
    ...(skippedQuantity > 0
      ? [
          {
            label: 'ITEMS_SKIPPED',
            subLabel: '',
            quantity: skippedQuantity,
          },
        ]
      : []),
    ...(nonPickableQuantity > 0
      ? [
          {
            label: 'NON_PICKABLE_ITEMS',
            subLabel: `${t(`${translationRoot}.NON_PICKABLE_ITEMS_INCLUDE`)}`,
            quantity: nonPickableQuantity,
          },
        ]
      : []),
  ];

  return (
    <section className={styles.section}>
      <article>
        <div className={styles.quantityLine}>
          <CheckmarkCircleIcon />
          <h6
            className={mergeClassNames(styles.quantity, currentLanguage === LANGUAGE_SPANISH && styles.quantitySpanish)}
          >
            {t(`${translationRoot}.COMPLETE`)}
          </h6>
        </div>

        {lineItems.map(line => (
          <div className={styles.lineItem} key={`${line.label}-${line.quantity}`}>
            <span>{t(`${translationRoot}.${line.label}`)}</span>
            <h6 className={styles.lineItemCount}>{line.quantity}</h6>
            <div className={styles.lineNonPickable}>{line.subLabel}</div>
          </div>
        ))}

        {/* EDIT ORDER FLOW */}
        {commercialSkipItemsAction === SKIP_ITEMS_ACTION_EDIT_ORDER && (
          <>
            {renderOrderSection({
              orders: unsuccessfullyPickedOrders,
              titleKey: 'UNSUCCESSFULLY_PICKED_ORDERS',
              messageKey: 'UNSUCCESSFULLY_PICKED_ORDERS_MESSAGE',
              isSuccess: false,
            })}

            {renderOrderSection({
              orders: successfullyPickedOrders,
              titleKey: 'SUCCESSFULLY_PICKED_ORDERS',
              messageKey: 'SUCCESSFULLY_PICKED_ORDERS_MESSAGE',
              isSuccess: true,
            })}

            {renderOrderSection({
              orders: editingOrders,
              titleKey: 'ORDERS_BEING_EDITED_IN_ZNET',
              messageKey: 'ORDERS_EDITED_IN_ZNET_MESSAGE',
              isSuccess: false,
              keyField: 'orderId',
              destinationField: 'shopName',
            })}
          </>
        )}

        {/* FINALIZE FLOW */}
        {commercialSkipItemsAction === SKIP_ITEMS_ACTION_FINALIZE && (
          <>
            {unsuccessfullyPickedOrders.length > 0 && (
              <div className={styles.lineItem}>
                <span>{t(`${translationRoot}.PLEASE_FULLFIL_SKIPPED_ITEMS`)}</span>
                {unsuccessfullyPickedOrders.map(order => (
                  <div key={order.orderID} className={styles.orderDestination}>
                    {order.destination}
                  </div>
                ))}
                {completedQuantityFromFinalizedOrders > 0 && successfullyPickedOrders.length === 0 && (
                  <p className={styles.lineItemMessageSuccess}>
                    {t(`${translationRoot}.SUCCESSFULLY_PICKED_ORDERS_MESSAGE`)}
                  </p>
                )}
              </div>
            )}
            {renderOrderSection({
              orders: successfullyPickedOrders,
              titleKey: 'SUCCESSFULLY_PICKED_ORDERS',
              messageKey: 'SUCCESSFULLY_PICKED_ORDERS_MESSAGE',
              isSuccess: true,
            })}

            {renderOrderSection({
              orders: editingOrders,
              titleKey: 'ORDERS_BEING_EDITED_IN_ZNET',
              messageKey: 'ORDERS_EDITED_IN_ZNET_MESSAGE',
              isSuccess: false,
              keyField: 'orderId',
              destinationField: 'shopName',
            })}
          </>
        )}

        {shouldShowNonPickableMessage && (
          <div className={styles.attentionMessage}>
            <span>{t(`${translationRoot}.NON_PICKABLE_INVOICE_INCLUDED`)}</span>
          </div>
        )}
      </article>
      <footer>
        <Button data-cy="pick-tour-complete-button" onClick={returnToOrderList}>
          {t(`${translationRoot}.RETURN`)}
        </Button>
      </footer>
    </section>
  );
};

export default CommercialPickTourComplete;
